// ─────────────────────────────────────────────────────────────────────────────
// LEGACY DATA POLICY — see LEGACY_DATA_POLICY.md (locked)
//
// KinsCircle preserves legacy family data permanently unless deleted by the
// family owner/admin. Subscription status (plan / role / stripe_*_id /
// status) controls ACCESS and FEATURES only — it MUST NEVER delete, archive,
// or purge family trees, profiles, relationships, photos, videos, memories,
// notes, comments, board posts, or invites.
//
// In this file specifically:
//   • Reading `isMember` / `isPaid` / `plan` / `role` may gate UI (e.g.
//     show MembershipModal, render MembershipLock, hide an upload button).
//   • This context MUST NEVER call deletePerson, deleteMember, deletePhoto,
//     deleteComment, supabase.from(...).delete(), or supabase.storage....remove()
//     in response to a billing-state transition.
//   • A user moving from paid → unpaid → paid again must read EXACTLY the
//     same family graph on both sides of the lapse.
// ─────────────────────────────────────────────────────────────────────────────
// Backwards-compat shim: MembershipContext now wraps AuthContext so all
// existing `useMembership()` consumers keep working without changes.
//
// `isMember` is true ONLY when the user is authenticated AND on a paid plan
// (monthly_premium, annual_founding, or annual_standard). Owners and admins are also treated
// as members regardless of plan, because they always have full access.
// Free users are treated as authenticated-but-not-member, so paid-only UI
// continues to lock for them.

import React, { createContext, useContext, useCallback, useState, ReactNode } from 'react';
import { AuthProvider, useAuth, type Plan, type Role } from '@/contexts/AuthContext';
import { supabase } from '@/lib/supabase';
import MembershipModal from '@/components/MembershipModal';
import type { PlanId } from '@/lib/business/KinsCircleBusinessEngine';
import AuthModal from '@/components/AuthModal';

export type BillingCycle = 'monthly' | 'yearly';

interface MembershipContextType {
  isMember: boolean;
  isPaid: boolean;
  hasSponsoredSeat: boolean;
  hasPaidCapability: boolean;
  entitlementSource:
    | 'stripe_subscription'
    | 'protected_owner'
    | 'sponsored_seat'
    | 'manual_grant'
    | 'none';
  isProtectedEntitlement: boolean;
  isStripeBacked: boolean;
  isAuthenticated: boolean;
  isOwner: boolean;
  isAdmin: boolean;
  isStaff: boolean;
  plan: Plan;
  role: Role;
  memberEmail: string | null;
  memberName: string | null;
  familyName: string | null;
  loading: boolean;
  profileLoading: boolean;
  profileError: string | null;
  /**
   * Open the membership/checkout modal. Pass an explicit billing cycle to
   * skip the in-modal plan picker — used by the Pricing section so each CTA
   * routes to its own clearly-labelled flow:
   *   - 'monthly' → $4.99/mo with a 3-day free trial (trial ONLY here)
   *   - 'yearly'  → $14.99/yr Founder's Discount, NO trial, ever
   * Omit the argument (or pass undefined) to make the user pick in-modal.
   */
  openMembershipModal: (planId?: PlanId, cycle?: BillingCycle) => void;
  closeMembershipModal: () => void;
  openAuthModal: (mode?: 'signin' | 'signup') => void;
  closeAuthModal: () => void;
  requireMembership: (onUnlock: () => void) => void;
  refreshMembership: () => Promise<void>;
  setFamilyName: (name: string) => void;
  signOut: () => Promise<void>;
}

const MembershipContext = createContext<MembershipContextType | undefined>(undefined);

const Inner: React.FC<{ children: ReactNode }> = ({ children }) => {
  const auth = useAuth();
  const [paymentOpen, setPaymentOpen] = useState(false);
  const [initialCycle, setInitialCycle] =
    useState<BillingCycle | undefined>(undefined);
  const [initialPlanId, setInitialPlanId] =
    useState<PlanId | undefined>(undefined);
  const [authOpen, setAuthOpen] = useState(false);
  const [authMode, setAuthMode] = useState<'signin' | 'signup'>('signin');
  const [hasSponsoredSeat, setHasSponsoredSeat] =
    useState(false);
  const [capabilityLoading, setCapabilityLoading] =
    useState(false);

  type MembershipEntitlementProjection = {
    plan?: Plan;
    status?: string;
    source?:
      | 'stripe_subscription'
      | 'protected_owner'
      | 'sponsored_seat'
      | 'manual_grant'
      | 'none';
    seatsTotal?: number;
    protected?: boolean;
    stripeBacked?: boolean;
    stripeCustomerId?: string | null;
    stripeSubscriptionId?: string | null;
  };

  const [
    entitlement,
    setEntitlement,
  ] = useState<MembershipEntitlementProjection | null>(
    null,
  );

  const [
    entitlementLoading,
    setEntitlementLoading,
  ] = useState(false);

  const [
    membershipRefreshVersion,
    setMembershipRefreshVersion,
  ] = useState(0);

  const paidPlans = new Set<Plan>([
    'monthly_premium',
    'annual_founding',
    'annual_standard',
  ]);

  const entitlementStatusActive =
    entitlement?.status === 'active' ||
    entitlement?.status === 'trialing';

  const effectivePlan =
    entitlement?.plan &&
    entitlement.source !== 'none'
      ? entitlement.plan
      : auth.plan;

  const authorityIsPaid =
    entitlement !== null
      ? entitlementStatusActive &&
        paidPlans.has(effectivePlan)
      : auth.isPaid;

  const entitlementSource =
    entitlement?.source ?? 'none';

  const isProtectedEntitlement =
    entitlement?.protected === true;

  const isStripeBacked =
    entitlement?.stripeBacked === true;

  // Members for the purpose of unlocking paid features:
  // - Anyone on a paid plan
  // - Anyone holding an active sponsored seat
  // - OR anyone with an admin/owner role
  const hasPaidCapability =
    authorityIsPaid ||
    auth.isStaff ||
    hasSponsoredSeat;

  const isMember = hasPaidCapability;

  React.useEffect(() => {
    let cancelled = false;

    const loadMembershipAuthorities =
      async () => {
        if (!auth.isAuthenticated) {
          setHasSponsoredSeat(false);
          setEntitlement(null);
          setCapabilityLoading(false);
          setEntitlementLoading(false);
          return;
        }

        setCapabilityLoading(true);
        setEntitlementLoading(true);

        const [
          capabilityResponse,
          entitlementResponse,
        ] = await Promise.all([
          supabase.rpc(
            'get_current_family_capability',
          ),
          supabase.rpc(
            'get_my_membership_entitlement',
          ),
        ]);

        if (cancelled) return;

        if (capabilityResponse.error) {
          console.error(
            '[MembershipContext] capability projection failed',
            capabilityResponse.error,
          );
          setHasSponsoredSeat(false);
        } else {
          const capability =
            capabilityResponse.data &&
            typeof capabilityResponse.data ===
              'object'
              ? capabilityResponse.data as {
                  has_sponsored_seat?: boolean;
                }
              : null;

          setHasSponsoredSeat(
            capability?.has_sponsored_seat ===
              true,
          );
        }

        if (entitlementResponse.error) {
          console.error(
            '[MembershipContext] entitlement projection failed; using profile fallback',
            entitlementResponse.error,
          );
          setEntitlement(null);
        } else {
          const projected =
            entitlementResponse.data &&
            typeof entitlementResponse.data ===
              'object'
              ? entitlementResponse.data as
                  MembershipEntitlementProjection
              : null;

          setEntitlement(projected);
        }

        setCapabilityLoading(false);
        setEntitlementLoading(false);
      };

    void loadMembershipAuthorities();

    return () => {
      cancelled = true;
    };
  }, [
    auth.isAuthenticated,
    auth.memberEmail,
    auth.plan,
    membershipRefreshVersion,
  ]);

  // ───────── Modal coordination (UI-only) ─────────
  // The Premium plan modal must take priority over ActivationOverlay /
  // onboarding modals after sign-in. We broadcast its open/close state on
  // the window so ActivationOverlay (and any other lower-priority modal)
  // can suppress itself while it's open. Purely additive — no DB writes,
  // no Stripe / auth / billing logic touched. See ActivationOverlay for
  // the corresponding listener.
  React.useEffect(() => {
    try {
      window.dispatchEvent(
        new CustomEvent(paymentOpen ? 'kc:premium-modal-open' : 'kc:premium-modal-close'),
      );
    } catch {
      /* ignore */
    }
    // Belt-and-suspenders cleanup when the premium modal closes: if a
    // previously-stacked overlay left body styles locked, clear them on
    // the next tick so the page never stays blurred / non-interactive.
    if (!paymentOpen) {
      const t = window.setTimeout(() => {
        try {
          if (document.body.style.pointerEvents === 'none') {
            document.body.style.pointerEvents = '';
          }
          // Only clear overflow if NOTHING else is currently rendering an
          // open Radix dialog (which would set data-state="open" on a
          // [data-radix-dialog-overlay]). Avoids fighting other modals.
          const anyOpen = document.querySelector(
            '[data-radix-dialog-overlay][data-state="open"], [data-state="open"][role="dialog"]',
          );
          if (!anyOpen && document.body.style.overflow === 'hidden') {
            document.body.style.overflow = '';
          }
        } catch {
          /* ignore */
        }
      }, 350);
      return () => window.clearTimeout(t);
    }
  }, [paymentOpen]);

  const openMembershipModal = useCallback(
    (planId?: PlanId, cycle?: BillingCycle) => {
      if (!auth.isAuthenticated) {
        setAuthMode('signup');
        setAuthOpen(true);
        return;
      }

      if (authorityIsPaid || auth.isStaff) {
        return;
      }

      setInitialPlanId(planId);
      setInitialCycle(cycle);
      setPaymentOpen(true);
    },
    [
      auth.isAuthenticated,
      authorityIsPaid,
      auth.isStaff,
    ]
  );

  // Listen for the post-signup "open trial checkout" event dispatched by
  // AuthModal, so brand-new accounts get sent straight into checkout
  // without an extra click. The dispatched event MAY include a billing
  // cycle in `event.detail.cycle`; if absent, we open the picker so the
  // user makes an explicit monthly-vs-yearly choice (per the strict
  // separation rule — the modal must NEVER assume a trial unless monthly
  // is explicitly selected).
  //
  // Hardened against duplicate-fire bugs: React 18 StrictMode + the
  // Login.tsx redirect path can both dispatch this event in quick
  // succession. We ignore the event entirely if (a) the user is already
  // paid/staff, or (b) the modal is already open, so we never end up
  // with stacked Premium dialogs.
  const paymentOpenRef = React.useRef(paymentOpen);
  React.useEffect(() => {
    paymentOpenRef.current = paymentOpen;
  }, [paymentOpen]);

  React.useEffect(() => {
    const handler = (ev: Event) => {
      if (!auth.isAuthenticated) return;
      if (auth.isPaid || auth.isStaff) return; // already subscribed → no modal
      if (paymentOpenRef.current) return; // dedupe: already showing
      const detail = (ev as CustomEvent).detail as
        | { cycle?: BillingCycle; planId?: PlanId }
        | undefined;
      setInitialPlanId(detail?.planId);
      setInitialCycle(detail?.cycle);
      setPaymentOpen(true);
    };
    window.addEventListener('kc:open-trial-checkout', handler as EventListener);
    return () => window.removeEventListener('kc:open-trial-checkout', handler as EventListener);
  }, [
    auth.isAuthenticated,
    authorityIsPaid,
    auth.isStaff,
  ]);

  const closeMembershipModal = useCallback(() => {
    setPaymentOpen(false);
    setInitialPlanId(undefined);
    setInitialCycle(undefined);
  }, []);

  const openAuthModal = useCallback((mode: 'signin' | 'signup' = 'signin') => {
    setAuthMode(mode);
    setAuthOpen(true);
  }, []);
  const closeAuthModal = useCallback(() => setAuthOpen(false), []);



  const requireMembership = useCallback(
    (onUnlock: () => void) => {
      if (hasPaidCapability) {
        onUnlock();
      } else if (!auth.isAuthenticated) {
        setAuthMode('signup');
        setAuthOpen(true);
      } else {
        setInitialPlanId(undefined);
        setInitialCycle(undefined);
        setPaymentOpen(true);
      }
    },
    [
      auth.isAuthenticated,
      auth.isPaid,
      auth.isStaff,
      hasPaidCapability,
    ]
  );

  const refreshMembership = useCallback(async () => {
    await auth.refreshProfile();
    setMembershipRefreshVersion(
      (current) => current + 1,
    );
  }, [auth]);

  const setFamilyName = useCallback(
    (name: string) => {
      auth.setFamilyName(name).catch((e) => console.error(e));
    },
    [auth]
  );

  const value: MembershipContextType = {
    isMember,
    isPaid: authorityIsPaid,
    hasSponsoredSeat,
    hasPaidCapability,
    entitlementSource:
      hasSponsoredSeat &&
      !authorityIsPaid
        ? 'sponsored_seat'
        : entitlementSource,
    isProtectedEntitlement,
    isStripeBacked,
    isAuthenticated: auth.isAuthenticated,
    isOwner: auth.isOwner,
    isAdmin: auth.isAdmin,
    isStaff: auth.isStaff,
    plan: effectivePlan,
    role: auth.role,
    memberEmail: auth.memberEmail,
    memberName: auth.memberName,
    familyName: auth.familyName,
    loading:
      auth.loading ||
      capabilityLoading ||
      entitlementLoading,
    profileLoading: auth.profileLoading,
    profileError: auth.profileError,
    openMembershipModal,
    closeMembershipModal,
    openAuthModal,
    closeAuthModal,
    requireMembership,
    refreshMembership,
    setFamilyName,
    signOut: auth.signOut,
  };

  return (
    <MembershipContext.Provider value={value}>
      {children}
      <MembershipModal
        open={paymentOpen}
        onOpenChange={setPaymentOpen}
        initialPlanId={initialPlanId}
        initialCycle={initialCycle}
      />
      <AuthModal
        open={authOpen}
        mode={authMode}
        onOpenChange={setAuthOpen}
        onSwitchMode={setAuthMode}
      />
    </MembershipContext.Provider>
  );
};

export const MembershipProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  return (
    <AuthProvider>
      <Inner>{children}</Inner>
    </AuthProvider>
  );
};

export const useMembership = () => {
  const ctx = useContext(MembershipContext);
  if (!ctx) throw new Error('useMembership must be used within MembershipProvider');
  return ctx;
};
